import {makeAutoObservable} from "mobx";
import {ValidatedValue} from "../../helpers/validatedValue";
import {PriceListModel} from "../models/priceList";
import {IParkingRateInStore, ParkingRateInStore} from "./parkingRateInStore";
import Validator from "../../helpers/validator";
import {errorMessages} from "../../helpers/errorMessage";
import i18n from "../../i18n/i18n";
import {RelationType} from "../../transportation/models/relation";

export interface IPriceInStore {
    clearStore: () => void;
    createParkingRate: () => void;
    removeParkingRate: (rateId: number) => void;

    priceListType: ValidatedValue<RelationType | "">;
    setPriceListType: (priceListType: RelationType | "") => void;
    startDate: ValidatedValue<Date | null>;
    setStartDate: (startDate: Date | null) => void;
    parkingFeeForOther: ValidatedValue<string | number | null>;
    setParkingFeeForOther: (parkingFee: string | number | null) => void;

    parkingRates: IParkingRateInStore[];

    priceIsValid: () => boolean;
    setPrice: (priceDate: PriceListModel | null | undefined) => void;
}

export class PriceInStore implements IPriceInStore {
    _priceListType: ValidatedValue<RelationType | "">;
    _startDate: ValidatedValue<Date | null>;
    _parkingFeeForOther: ValidatedValue<string | number | null>;
    _parkingRates: IParkingRateInStore[] = [];

    constructor() {
        makeAutoObservable(this);

        const notEmptyValidator = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        const correctDateValidators = {
            name: "dateIsCorrect",
            fn: Validator.isCorrectDateWithTime,
            errorMessage: () => i18n.t("errorMessages.correctDate")
        };

        const dateValidFrom = {
            name: "dateValidFrom",
            fn: (date: Date) => {
                if (date && Validator.isCorrectDateWithTime(date)) {
                    return date.getTime() > Date.now()+1;
                } return false;
            },
            errorMessage: () => i18n.t("priceList.errors.dateValidFrom")
        };

        const valueHasNoMoreThanTwoDecimalPlacesOrEmptyAndPositiveNumber = {
            name: "valueHasNoMoreThanTwoDecimalPlacesOrEmptyAndPositiveNumber",
            fn: (value: number) => {
                return Validator.isOnlyPositiveNumberWithCommaOrEmpty(value.toString()) && Validator.hasNoMoreThanTwoDecimalPlacesOrEmpty(value.toString());
            },
            errorMessage: () => i18n.t("priceList.errors.hasNoMoreThanTwoDecimalPlacesOrEmptyAndPositiveNumber")
        };

        const numberGreaterThanZero = {
            name: "numberGreaterThanZero",
            fn: (value: number) => {
                return Validator.numberGreaterThanZero(value);
            },
            errorMessage: () => i18n.t("priceList.errors.numberGreaterThanZero")
        };

        this._priceListType = new ValidatedValue("", [notEmptyValidator]);
        this._startDate = new ValidatedValue(null, [notEmptyValidator, correctDateValidators, dateValidFrom]);
        this._parkingFeeForOther = new ValidatedValue("", [notEmptyValidator, valueHasNoMoreThanTwoDecimalPlacesOrEmptyAndPositiveNumber, numberGreaterThanZero]);

        if (this.parkingRates.length === 0) {
            this.createParkingRate();
        }
    }

    createParkingRate() {
        this._parkingRates.push(new ParkingRateInStore());
    }

    removeParkingRate(rateId: number) {
        this._parkingRates.splice(rateId, 1);
    }

    clearStore() {
        this._priceListType.clearValidation();
        this._startDate.clearValidation();
        this._parkingFeeForOther.clearValidation();

        this._priceListType.setValue("");
        this._startDate.setValue(null);
        this._parkingFeeForOther.setValue("");
        this._parkingRates = [];
    }

    get priceListType() {
        return this._priceListType;
    }

    setPriceListType(priceListType: "" | RelationType) {
        this._priceListType.setValue(priceListType);
    }

    get startDate() {
        return this._startDate;
    }

    setStartDate(startDate: Date | null) {
        this._startDate.setValue(startDate);
    }

    get parkingRates() {
        return this._parkingRates;
    }

    get parkingFeeForOther() {
        return this._parkingFeeForOther;
    }

    setParkingFeeForOther(parkingFee: string | number | null) {
        this._parkingFeeForOther.setValue(parkingFee);
    }

    priceIsValid(): boolean {
        this._priceListType.validate();
        this._startDate.validate();
        this._parkingFeeForOther.validate();
        const parkingRates = this._parkingRates;

        let isValidParkingRates = true;

        parkingRates.forEach((parkingRate) => {
            if (!this.parkingRatesAreValid(parkingRate)) {
                isValidParkingRates = false;
            }
        });

        return this._priceListType.isValid && this._startDate.isValid && this._parkingFeeForOther.isValid && isValidParkingRates;
    }

    parkingRatesAreValid(parkingRate: IParkingRateInStore) {
        parkingRate.parkingTime.validate();
        parkingRate.parkingFee.validate();

        return parkingRate.parkingTime.isValid && parkingRate.parkingFee.isValid;
    }

    setPrice(priceDate: PriceListModel | null | undefined) {
        if (!priceDate) return;

        if (this.parkingRates.length > 0) {
            this._parkingRates = [];
        }

        this.setPriceListType(priceDate.priceListType);
        this.setStartDate(new Date(priceDate.startDate));

        const parkingRates = priceDate.parkingRates;
        const length = parkingRates?.length || 0;

        if (!length) {
            this.createParkingRate();
        }

        for(let i = 0; i < length; i++) {
            if (parkingRates[i].parkingTime === null) {
                this.setParkingFeeForOther(parkingRates[i].parkingFee);
            } else {
                this.createParkingRate();
                this.parkingRates[i].setParkingTime(parkingRates![i].parkingTime || "");
                this.parkingRates[i].setParkingFee(parkingRates![i].parkingFee || "");
            }
        }
    }
}